import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, addDoc, serverTimestamp, where, arrayUnion } from 'firebase/firestore';

const STATUS_OPTIONS = ['pending', 'quoted', 'active', 'complete', 'invoiced', 'paid'];
const STATUS_LABELS = { pending: 'Pending', quoted: 'Quoted', active: 'Active', complete: 'Complete', invoiced: 'Invoiced', paid: 'Paid' };
const JOB_TYPE_LABELS = { 'one-off': 'One-off', weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' };

function Detail({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: '0.84rem' }}>{value}</div>
    </div>
  );
}

// Defined at module scope (not inside AdminJobs) so it keeps a stable
// component identity across renders — otherwise every keystroke in a
// child input (work log, invoicing, notes, etc.) re-renders AdminJobs,
// which would recreate this function and force React to unmount/remount
// the whole card, dropping focus out of whatever field was being typed in.
function JobCard({
  job,
  expandedJob, setExpandedJob,
  editingType, typeDraft, setEditingType, setTypeDraft, setJobType,
  clearingLogJob, setClearingLogJob, clearWorkLog,
  loggingEntry, setLoggingEntry, entryDraft, setEntryDraft, openLogForm, saveLogEntry, removeLogEntry,
  invoicingFromLog, setInvoicingFromLog, logRate, setLogRate, createInvoiceFromLog,
  schedulingJob, setSchedulingJob, nextDateDraft, setNextDateDraft, saveNextDate,
  invoicing, setInvoicing, invoiceAmount, setInvoiceAmount, invoiceDesc, setInvoiceDesc, saving, createInvoice,
  noteEditJob, setNoteEditJob, noteDraft, setNoteDraft, saveNotes,
}) {
  const isExpanded = expandedJob === job.id;
  const isRecurring = job.jobType && job.jobType !== 'one-off';
  const typeLabel = JOB_TYPE_LABELS[job.jobType] || '';
  const totalHours = (job.workLog || []).filter(e => e.type === 'labour').reduce((s, e) => s + e.hours, 0);
  const totalExpenses = (job.workLog || []).filter(e => e.type === 'expense').reduce((s, e) => s + e.amount, 0);
  return (
    <div className="card" style={{ marginBottom: 12, borderLeft: `4px solid ${job.status === 'active' ? 'var(--green-mid)' : job.status === 'complete' ? 'var(--gold)' : 'var(--border)'}` }}>

      {/* Tappable summary header */}
      <div onClick={() => setExpandedJob(isExpanded ? null : job.id)} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div style={{ flex: 1, marginRight: 8 }}>
            <div style={{ fontWeight: 700 }}>{job.name}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{job.address}</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--green-dark)', fontWeight: 600, marginTop: 2 }}>{job.services?.join(', ')}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <span className={`badge badge-${job.status}`}>{STATUS_LABELS[job.status]}</span>
            {typeLabel && (
              <span style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                background: isRecurring ? '#E3F2FD' : 'var(--bg)',
                color: isRecurring ? '#1565C0' : 'var(--text-muted)',
                border: `1px solid ${isRecurring ? '#90CAF9' : 'var(--border)'}` }}>
                {isRecurring ? '🔄 ' : ''}{typeLabel}
              </span>
            )}
          </div>
        </div>
        {job.quoteAmount && (
          <div style={{ fontSize: '0.85rem', marginBottom: 4 }}>Agreed: <strong>£{job.quoteAmount.toFixed(2)}</strong></div>
        )}
        {(totalHours > 0 || totalExpenses > 0) && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
            🕐 {totalHours} hrs · 💷 £{totalExpenses.toFixed(2)} expenses
          </div>
        )}
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
          {isExpanded ? '▲ Less' : '▼ Details'}
        </div>
      </div>

      {/* Expanded detail panel */}
      {isExpanded && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 6 }}>

          {/* Contact */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px', marginBottom: 10 }}>
            <Detail label="Email" value={job.email} />
            <Detail label="Phone" value={job.phone || '—'} />
          </div>

          <Detail label="Address" value={job.address} />

          {/* Services as chips */}
          {job.services?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>Services</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {job.services.map(s => (
                  <span key={s} style={{ background: 'var(--green-pale)', color: 'var(--green-dark)', borderRadius: 12, padding: '2px 9px', fontSize: '0.8rem', fontWeight: 500 }}>{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Dynamic fields */}
          {job.dynamicFields && Object.keys(job.dynamicFields).length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>Job details</div>
              {Object.entries(job.dynamicFields).map(([k, v]) => v ? (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', paddingBottom: 4, marginBottom: 4, borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{k}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ) : null)}
            </div>
          )}

          {job.description && <Detail label="Description" value={job.description} />}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px', marginBottom: 10 }}>
            <Detail label="Preferred date" value={job.preferredDate || '—'} />
            <Detail label="Pricing" value={job.pricingPreference === 'hourly' ? 'Hourly' : job.pricingPreference === 'either' ? 'Advise me' : 'Fixed price'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px', marginBottom: 10 }}>
            <Detail label="Submitted" value={job.createdAt?.toDate?.()?.toLocaleDateString('en-GB') || '—'} />
            <Detail label="Quoted" value={job.quotedAt?.toDate?.()?.toLocaleDateString('en-GB') || '—'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px', marginBottom: 10 }}>
            <Detail label="Quote amount" value={job.quoteAmount ? `£${job.quoteAmount.toFixed(2)}` : '—'} />
            <Detail label="Invoice amount" value={job.invoiceAmount ? `£${job.invoiceAmount.toFixed(2)}` : '—'} />
          </div>

          {job.lastInvoicedAt && (
            <Detail label="Last invoiced" value={job.lastInvoicedAt?.toDate?.()?.toLocaleDateString('en-GB')} />
          )}

          {/* Recurring toggle */}
          <div style={{ marginBottom: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Schedule</div>
            {editingType === job.id ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <select value={typeDraft} onChange={e => setTypeDraft(e.target.value)} style={{ flex: 1 }}>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                </select>
                <button className="btn-primary" onClick={() => setJobType(job.id, typeDraft)}
                  style={{ width: 'auto', padding: '7px 12px', fontSize: '0.8rem' }}>Save</button>
                <button onClick={() => setEditingType(null)}
                  style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: '0.8rem', cursor: 'pointer' }}>✕</button>
              </div>
            ) : isRecurring ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#E3F2FD', borderRadius: 8, padding: '6px 10px' }}>
                <span style={{ fontSize: '0.82rem', color: '#1565C0', flex: 1 }}>🔄 Recurring — <strong>{typeLabel}</strong></span>
                <button onClick={() => { setEditingType(job.id); setTypeDraft(job.jobType); }}
                  style={{ background: 'none', border: 'none', color: '#1565C0', fontSize: '0.75rem', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Change</button>
                <button onClick={() => setJobType(job.id, 'one-off')}
                  style={{ background: 'none', border: 'none', color: '#1565C0', fontSize: '0.75rem', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Make one-off</button>
              </div>
            ) : (
              <button onClick={() => { setEditingType(job.id); setTypeDraft('weekly'); }}
                style={{ width: '100%', background: 'none', border: '1px dashed var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: '0.8rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                🔄 Make recurring
              </button>
            )}
          </div>

          {/* Work log */}
          {(job.status === 'active' || job.status === 'complete') && (
            <div style={{ marginBottom: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Work log</div>
                {job.workLog?.length > 0 && (
                  clearingLogJob === job.id ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Clear all?</span>
                      <button onClick={() => clearWorkLog(job)}
                        style={{ background: 'none', border: 'none', color: '#C33', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', padding: 0 }}>Yes, clear</button>
                      <button onClick={() => setClearingLogJob(null)}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.72rem', cursor: 'pointer', padding: 0 }}>Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setClearingLogJob(job.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.72rem', cursor: 'pointer', padding: 0 }}>Clear log</button>
                  )
                )}
              </div>

              {job.workLog?.length > 0 ? (
                <>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 10, background: 'var(--bg)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: '0.85rem' }}>🕐 <strong>{totalHours}</strong> hrs</div>
                    <div style={{ fontSize: '0.85rem' }}>💷 <strong>£{totalExpenses.toFixed(2)}</strong> expenses</div>
                  </div>
                  {[...job.workLog].sort((a, b) => a.date.localeCompare(b.date)).map(entry => (
                    <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: '0.82rem' }}>
                      <div>
                        <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>
                          {new Date(entry.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </span>
                        {entry.type === 'labour'
                          ? <span>{entry.hours} hrs{entry.note ? ` — ${entry.note}` : ''}</span>
                          : <span>{entry.description} — £{entry.amount.toFixed(2)}</span>}
                      </div>
                      <button onClick={() => removeLogEntry(job, entry.id)}
                        style={{ background: 'none', border: 'none', color: '#C33', fontSize: '0.95rem', cursor: 'pointer', padding: '0 4px' }}>×</button>
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 8 }}>No entries yet</div>
              )}

              {loggingEntry?.jobId === job.id ? (
                <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 10, marginTop: 8 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    {loggingEntry.type === 'labour' ? 'Log labour' : 'Log expense'}
                  </div>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label>Date</label>
                    <input type="date" value={entryDraft.date} onChange={e => setEntryDraft(d => ({ ...d, date: e.target.value }))} />
                  </div>
                  {loggingEntry.type === 'labour' ? (
                    <>
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <label>Hours</label>
                        <input type="number" value={entryDraft.hours} onChange={e => setEntryDraft(d => ({ ...d, hours: e.target.value }))} placeholder="e.g. 3.5" min="0" step="0.25" />
                      </div>
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <label>Note (optional)</label>
                        <input value={entryDraft.note} onChange={e => setEntryDraft(d => ({ ...d, note: e.target.value }))} placeholder="e.g. Hedge cutting, north boundary" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <label>Description</label>
                        <input value={entryDraft.description} onChange={e => setEntryDraft(d => ({ ...d, description: e.target.value }))} placeholder="e.g. Fence post hire" />
                      </div>
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <label>Amount (£)</label>
                        <input type="number" value={entryDraft.amount} onChange={e => setEntryDraft(d => ({ ...d, amount: e.target.value }))} placeholder="0.00" min="0" step="0.01" />
                      </div>
                    </>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-primary" onClick={saveLogEntry} style={{ flex: 1, padding: '8px', fontSize: '0.82rem' }}>Save</button>
                    <button onClick={() => setLoggingEntry(null)}
                      style={{ flex: 1, padding: '8px', background: 'white', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.82rem', cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => openLogForm(job.id, 'labour')}
                    style={{ flex: 1, background: 'none', border: '1px dashed var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: '0.78rem', color: 'var(--text-muted)', cursor: 'pointer' }}>+ Log labour</button>
                  <button onClick={() => openLogForm(job.id, 'expense')}
                    style={{ flex: 1, background: 'none', border: '1px dashed var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: '0.78rem', color: 'var(--text-muted)', cursor: 'pointer' }}>+ Log expense</button>
                </div>
              )}

              {job.workLog?.length > 0 && (
                invoicingFromLog === job.id ? (
                  <div style={{ marginTop: 10, background: '#F0F7F2', borderRadius: 8, padding: 10 }}>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label>Hourly rate for this job (£)</label>
                      <input type="number" value={logRate} onChange={e => setLogRate(e.target.value)} placeholder="e.g. 35" min="0" step="0.01" autoFocus />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn-primary" onClick={() => createInvoiceFromLog(job)}
                        disabled={!logRate || parseFloat(logRate) <= 0} style={{ flex: 1 }}>Continue to invoice</button>
                      <button onClick={() => { setInvoicingFromLog(null); setLogRate(''); }}
                        style={{ flex: 1, background: 'white', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn-primary" onClick={() => setInvoicingFromLog(job.id)} style={{ marginTop: 10, width: '100%' }}>
                    📄 Create invoice from log
                  </button>
                )
              )}
            </div>
          )}

          {/* Next date scheduling for recurring */}
          {isRecurring && (
            <div style={{ marginBottom: 12 }}>
              {schedulingJob === job.id ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="date" value={nextDateDraft} onChange={e => setNextDateDraft(e.target.value)}
                    style={{ flex: 1, fontSize: '0.85rem' }} />
                  <button onClick={() => saveNextDate(job.id)} className="btn-primary"
                    style={{ width: 'auto', padding: '7px 12px', fontSize: '0.8rem' }}>Save</button>
                  <button onClick={() => setSchedulingJob(null)}
                    style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: '0.8rem', cursor: 'pointer' }}>✕</button>
                </div>
              ) : job.nextDate ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#E3F2FD', borderRadius: 8, padding: '6px 10px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#1565C0', flex: 1 }}>
                    📅 Next visit: <strong>{new Date(job.nextDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</strong>
                  </span>
                  <button onClick={() => { setSchedulingJob(job.id); setNextDateDraft(job.nextDate); }}
                    style={{ background: 'none', border: 'none', color: '#1565C0', fontSize: '0.75rem', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Edit</button>
                </div>
              ) : (
                <button onClick={() => { setSchedulingJob(job.id); setNextDateDraft(''); }}
                  style={{ width: '100%', background: 'none', border: '1px dashed var(--border)', borderRadius: 8, padding: '5px 10px', fontSize: '0.8rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  📅 Schedule next visit
                </button>
              )}
            </div>
          )}

          {/* Actions */}
          {job.status === 'active' && invoicing !== job.id && (
            <button onClick={() => updateDoc(doc(db, 'quotes', job.id), { status: 'complete' })}
              style={{ marginBottom: 8, padding: '9px 16px', background: 'var(--gold)', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
              ✓ Mark complete
            </button>
          )}
          {job.status === 'complete' && invoicing !== job.id && (
            <button className="btn-primary"
              onClick={() => { setInvoicing(job.id); setInvoiceAmount(job.quoteAmount?.toFixed(2) || ''); setInvoiceDesc(job.services?.join(', ') || ''); }}
              style={{ marginBottom: 8 }}>
              📄 {isRecurring ? 'Invoice this cycle' : 'Create invoice'}
            </button>
          )}
          {invoicing === job.id && (
            <div style={{ marginBottom: 10 }}>
              {isRecurring && (
                <div style={{ background: '#E3F2FD', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: '0.9rem', color: '#1565C0' }}>
                  🔄 Recurring — invoicing this cycle resets the job to active for the next occurrence.
                </div>
              )}
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label>Invoice amount (£)</label>
                <input type="number" value={invoiceAmount} onChange={e => setInvoiceAmount(e.target.value)} placeholder="0.00" min="0" step="0.01" />
              </div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label>Description</label>
                <input type="text" value={invoiceDesc} onChange={e => setInvoiceDesc(e.target.value)} placeholder="Services description" />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-primary" onClick={() => createInvoice(job)} disabled={saving} style={{ flex: 1 }}>
                  {saving ? 'Sending...' : 'Send invoice'}
                </button>
                <button onClick={() => setInvoicing(null)}
                  style={{ flex: 1, padding: '12px', background: 'white', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Internal notes */}
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Internal notes</div>
            {noteEditJob === job.id ? (
              <div>
                <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
                  placeholder="Notes (not visible to client)…"
                  rows={3} style={{ width: '100%', fontSize: '0.82rem', resize: 'vertical', marginBottom: 6 }} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => saveNotes(job.id)} className="btn-primary"
                    style={{ flex: 1, padding: '8px', fontSize: '0.82rem' }}>Save</button>
                  <button onClick={() => setNoteEditJob(null)}
                    style={{ flex: 1, padding: '8px', background: 'white', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.82rem', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, fontSize: '0.82rem', color: job.notes ? 'var(--text-secondary)' : 'var(--text-muted)', fontStyle: job.notes ? 'italic' : 'normal' }}>
                  {job.notes || 'No notes yet'}
                </div>
                <button onClick={() => { setNoteEditJob(job.id); setNoteDraft(job.notes || ''); }}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.75rem', cursor: 'pointer', padding: 0, flexShrink: 0, fontWeight: 600 }}>
                  {job.notes ? 'Edit' : '+ Add note'}
                </button>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

function AdminNav({ active }) {
  return (
    <nav className="bottom-nav">
      <Link to="/admin/dashboard" className={active === 'quotes' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
        Quotes
      </Link>
      <Link to="/admin/jobs" className={active === 'jobs' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
        Jobs
      </Link>
      <Link to="/admin/messages" className={active === 'messages' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        Messages
      </Link>
      <Link to="/admin/clients" className={active === 'clients' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
        Clients
      </Link>
      <Link to="/admin/invoices" className={active === 'invoices' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        Invoices
      </Link>
      <Link to="/admin/reminders" className={active === 'reminders' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>
        Reminders
      </Link>
      <Link to="/admin/expenses" className={active === 'expenses' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
        Expenses
      </Link>
    </nav>
  );
}

export function AdminDashboard() {
  const { logout, currentUser } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [resendingQuote, setResendingQuote] = useState(null);
  const [showOrphanCheck, setShowOrphanCheck] = useState(false);

  useEffect(() => {
    const u1 = onSnapshot(query(collection(db, 'quotes'), orderBy('createdAt', 'desc')),
      snap => setQuotes(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(query(collection(db, 'invoices')),
      snap => setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); };
  }, []);

  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const activeJobCount = quotes.filter(q => q.status === 'active').length;
  const invoicedThisMonth = invoices
    .filter(inv => inv.createdAt?.toDate?.() >= monthStart)
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const paidThisMonth = invoices
    .filter(inv => inv.paid && inv.paidAt?.toDate?.() >= monthStart)
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);

  const pending = quotes.filter(q => q.status === 'pending');
  const others = quotes.filter(q => q.status !== 'pending');

  // Temporary diagnostic — flags invoices whose quoteId no longer resolves
  // to an existing quote (e.g. after manually deleting quotes in Console).
  const orphanedInvoices = invoices.filter(inv => inv.quoteId && !quotes.some(q => q.id === inv.quoteId));

  async function sendQuote(id) {
    if (!quoteAmount) return;
    setSaving(true);
    await updateDoc(doc(db, 'quotes', id), {
      status: 'quoted',
      quoteAmount: parseFloat(quoteAmount),
      quotedAt: serverTimestamp(),
    });
    setSelected(null);
    setQuoteAmount('');
    setSaving(false);
  }

  async function updateStatus(id, status) {
    await updateDoc(doc(db, 'quotes', id), { status });
  }

  async function resendQuote(id) {
    setResendingQuote(id);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch('https://europe-west2-le-douit-horticulture.cloudfunctions.net/resendCommunication', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ type: 'quote', id }),
      });
      if (!res.ok) throw new Error();
    } catch {
      alert('Could not resend email. Please try again.');
    } finally {
      setResendingQuote(null);
    }
  }

  return (
    <>
      <div className="app-header">
        <div><h1>Garden & Groundworks</h1><div className="subtitle">Admin — quote requests</div></div>
        <button onClick={logout} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', padding: '6px 12px', fontSize: '0.8rem' }}>Sign out</button>
      </div>
      <div className="page-wrap" style={{ paddingTop: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
          <div className="card" style={{ textAlign: 'center', padding: '12px 6px' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--green-dark)', lineHeight: 1 }}>{activeJobCount}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 4 }}>Active jobs</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '12px 6px' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--gold)', lineHeight: 1 }}>£{invoicedThisMonth.toFixed(0)}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 4 }}>Invoiced</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '12px 6px' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--green-mid)', lineHeight: 1 }}>£{paidThisMonth.toFixed(0)}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 4 }}>Paid this month</div>
          </div>
        </div>

        {/* Temporary diagnostic: checks currently-loaded invoices/quotes for
            dangling quoteId references. Safe to remove once confirmed clean. */}
        <div style={{ marginBottom: 20 }}>
          <button className="btn-secondary" onClick={() => setShowOrphanCheck(s => !s)} style={{ width: 'auto', padding: '8px 14px', fontSize: '0.8rem' }}>
            {showOrphanCheck ? 'Hide' : 'Check for'} orphaned invoice references
          </button>
          {showOrphanCheck && (
            <div className="card" style={{ marginTop: 10, borderLeft: orphanedInvoices.length > 0 ? '4px solid #c0392b' : '4px solid var(--green-mid)' }}>
              {orphanedInvoices.length === 0 ? (
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', margin: 0 }}>
                  ✓ No orphaned invoices found — every invoice's quoteId resolves to an existing quote.
                </p>
              ) : (
                <>
                  <p style={{ fontSize: '1rem', color: '#c0392b', fontWeight: 600, marginBottom: 10 }}>
                    {orphanedInvoices.length} invoice{orphanedInvoices.length === 1 ? '' : 's'} reference{orphanedInvoices.length === 1 ? 's' : ''} a quote that no longer exists:
                  </p>
                  {orphanedInvoices.map(inv => (
                    <div key={inv.id} style={{ fontSize: '0.83rem', paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid var(--border)' }}>
                      <div><strong>{inv.clientName || inv.clientEmail}</strong> — £{(inv.amount || 0).toFixed(2)} {inv.paid ? '(paid)' : '(unpaid)'}</div>
                      <div style={{ color: 'var(--text-muted)' }}>Invoice ID: {inv.id} · missing quoteId: {inv.quoteId}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {pending.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <h2 className="section-title" style={{ margin: 0 }}>New requests</h2>
              <span style={{ background: 'var(--gold)', color: 'white', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>{pending.length}</span>
            </div>
            {pending.map(q => (
              <div key={q.id} className="card" style={{ marginBottom: 12, borderLeft: '4px solid var(--gold)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700 }}>{q.name}</div>
                  <span className="badge badge-pending">New</span>
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 4 }}>{q.email} {q.phone && `· ${q.phone}`}</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 4 }}>{q.address}</div>
                {q.services?.length > 0 && <div style={{ fontSize: '0.85rem', color: 'var(--green-dark)', fontWeight: 600, marginBottom: 4 }}>{q.services.join(', ')}</div>}
                <div style={{ fontSize: '1rem', marginBottom: 12, background: 'var(--bg)', padding: '8px 12px', borderRadius: 8 }}>{q.description}</div>
                {q.preferredDate && <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 12 }}>Preferred start: {q.preferredDate}</div>}
                {selected === q.id ? (
                  <div>
                    <div className="form-group">
                      <label>Quote amount (£)</label>
                      <input type="number" value={quoteAmount} onChange={e => setQuoteAmount(e.target.value)} placeholder="0.00" min="0" step="0.01" />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn-primary" onClick={() => sendQuote(q.id)} disabled={saving} style={{ flex: 1 }}>Send quote</button>
                      <button className="btn-secondary" onClick={() => setSelected(null)} style={{ flex: 1 }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn-primary" onClick={() => setSelected(q.id)}>Send a quote</button>
                )}
              </div>
            ))}
            <hr className="divider" />
          </>
        )}
        <h2 className="section-title">All jobs</h2>
        {others.length === 0 && pending.length === 0 && <div className="empty-state"><p>No quote requests yet.</p></div>}
        {others.map(q => {
          const isExp = expandedQuote === q.id;
          const JOB_TYPE_LABELS = { 'one-off': 'One-off', weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' };
          return (
            <div key={q.id} className="card" style={{ marginBottom: 10 }}>
              {/* Collapsed summary — tap to expand */}
              <div onClick={() => setExpandedQuote(isExp ? null : q.id)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <div style={{ flex: 1, marginRight: 8 }}>
                    <div style={{ fontWeight: 600 }}>{q.name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{q.address}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--green-dark)', fontWeight: 600, marginTop: 2 }}>{q.services?.join(', ')}</div>
                  </div>
                  <span className={`badge badge-${q.status}`}>{STATUS_LABELS[q.status]}</span>
                </div>
                {q.quoteAmount && <div style={{ fontSize: '0.85rem', color: 'var(--green-dark)', fontWeight: 600, marginBottom: 4 }}>Quote: £{q.quoteAmount.toFixed(2)}</div>}
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right' }}>{isExp ? '▲ Less' : '▼ Details'}</div>
              </div>

              {/* Expanded detail panel */}
              {isExp && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: 10 }}>
                    {[['Email', q.email], ['Phone', q.phone || '—'], ['Submitted', q.createdAt?.toDate?.()?.toLocaleDateString('en-GB') || '—'], ['Quoted', q.quotedAt?.toDate?.()?.toLocaleDateString('en-GB') || '—'], ['Pricing', q.pricingPreference === 'hourly' ? 'Hourly' : q.pricingPreference === 'either' ? 'Advise' : 'Fixed'], ['Job type', JOB_TYPE_LABELS[q.jobType] || '—']].map(([label, val]) => (
                      <div key={label} style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                        <div style={{ fontSize: '0.83rem' }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  {q.description && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Description</div>
                      <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', background: 'var(--bg)', padding: '8px 10px', borderRadius: 8 }}>{q.description}</div>
                    </div>
                  )}
                  {q.dynamicFields && Object.keys(q.dynamicFields).length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Job details</div>
                      {Object.entries(q.dynamicFields).map(([k, v]) => v ? (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', paddingBottom: 4, marginBottom: 4, borderBottom: '1px solid var(--border)' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>{k}</span>
                          <span style={{ fontWeight: 600 }}>{v}</span>
                        </div>
                      ) : null)}
                    </div>
                  )}
                </div>
              )}

              {/* Status-change buttons always visible */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {STATUS_OPTIONS.filter(s => s !== q.status).map(s => (
                  <button key={s} onClick={() => updateStatus(q.id, s)} style={{ padding: '5px 12px', fontSize: '0.8rem', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, fontWeight: 500, width: 'auto' }}>
                    → {STATUS_LABELS[s]}
                  </button>
                ))}
                <button onClick={() => resendQuote(q.id)} disabled={resendingQuote === q.id}
                  style={{ padding: '5px 12px', fontSize: '0.8rem', background: 'var(--green-pale)', color: 'var(--green-dark)', border: '1px solid var(--green-light)', borderRadius: 6, fontWeight: 600, width: 'auto' }}>
                  {resendingQuote === q.id ? 'Sending…' : '✉️ Resend'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <AdminNav active="quotes" />
    </>
  );
}

export function AdminJobs() {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [invoicing, setInvoicing] = useState(null);
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceDesc, setInvoiceDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [schedulingJob, setSchedulingJob] = useState(null);
  const [nextDateDraft, setNextDateDraft] = useState('');
  const [noteEditJob, setNoteEditJob] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [expandedJob, setExpandedJob] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addingClient, setAddingClient] = useState(false);
  const [addingForExistingClient, setAddingForExistingClient] = useState(false);
  const [clientForm, setClientForm] = useState({ name: '', email: '', phone: '', address: '', description: '' });
  const [addingClientSaving, setAddingClientSaving] = useState(false);
  const [addingClientError, setAddingClientError] = useState('');
  const [loggingEntry, setLoggingEntry] = useState(null); // { jobId, type: 'labour' | 'expense' }
  const [entryDraft, setEntryDraft] = useState({ date: '', hours: '', note: '', description: '', amount: '' });
  const [clearingLogJob, setClearingLogJob] = useState(null);
  const [invoicingFromLog, setInvoicingFromLog] = useState(null);
  const [logRate, setLogRate] = useState('');
  const [editingType, setEditingType] = useState(null);
  const [typeDraft, setTypeDraft] = useState('weekly');

  useEffect(() => {
    const q = query(
      collection(db, 'quotes'),
      where('status', 'in', ['active', 'complete', 'invoiced', 'paid'])
    );
    return onSnapshot(q, snap => setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  useEffect(() => {
    if (location.state?.openAddClientJob) {
      setAddingClient(true);
      if (location.state.prefillClient) {
        setClientForm({ ...location.state.prefillClient, description: '' });
        setAddingForExistingClient(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  async function addClientJob(e) {
    e.preventDefault();
    if (!clientForm.name || !clientForm.email || !clientForm.description) {
      setAddingClientError('Please fill in name, email and job description.');
      return;
    }
    setAddingClientSaving(true);
    setAddingClientError('');
    try {
      // Creates the client/job record only — no invoice, no email. Raise
      // the invoice later (once the job's marked ready) from here or the
      // Invoices tab, picking this job from the "Select job" dropdown.
      await addDoc(collection(db, 'quotes'), {
        name: clientForm.name,
        email: clientForm.email.trim().toLowerCase(),
        phone: clientForm.phone || '',
        address: clientForm.address || '',
        services: [],
        description: clientForm.description,
        jobType: 'one-off',
        source: 'manual',
        status: 'active',
        createdAt: serverTimestamp(),
      });
      setAddingClient(false);
      setAddingForExistingClient(false);
      setSuccess(`New job added for ${clientForm.name}`);
      setTimeout(() => setSuccess(null), 4000);
      setClientForm({ name: '', email: '', phone: '', address: '', description: '' });
    } catch (err) {
      setAddingClientError(err.message || 'Failed to add client & job. Please try again.');
    }
    setAddingClientSaving(false);
  }

  function openLogForm(jobId, type) {
    setLoggingEntry({ jobId, type });
    setEntryDraft({ date: new Date().toISOString().slice(0, 10), hours: '', note: '', description: '', amount: '' });
  }

  async function saveLogEntry() {
    const { jobId, type } = loggingEntry;
    if (type === 'labour') {
      if (!entryDraft.date || !entryDraft.hours) return;
    } else if (!entryDraft.date || !entryDraft.description || !entryDraft.amount) {
      return;
    }
    const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const entry = type === 'labour'
      ? { id, type, date: entryDraft.date, hours: parseFloat(entryDraft.hours), note: entryDraft.note || '' }
      : { id, type, date: entryDraft.date, description: entryDraft.description, amount: parseFloat(entryDraft.amount) };
    await updateDoc(doc(db, 'quotes', jobId), { workLog: arrayUnion(entry) });
    setLoggingEntry(null);
  }

  async function removeLogEntry(job, entryId) {
    await updateDoc(doc(db, 'quotes', job.id), { workLog: (job.workLog || []).filter(e => e.id !== entryId) });
  }

  async function clearWorkLog(job) {
    await updateDoc(doc(db, 'quotes', job.id), { workLog: [] });
    setClearingLogJob(null);
  }

  async function setJobType(jobId, jobType) {
    await updateDoc(doc(db, 'quotes', jobId), { jobType });
    setEditingType(null);
  }

  function createInvoiceFromLog(job) {
    const rate = parseFloat(logRate);
    if (!rate || rate <= 0) return;
    const totalHours = (job.workLog || []).filter(e => e.type === 'labour').reduce((s, e) => s + e.hours, 0);
    const expenses = (job.workLog || []).filter(e => e.type === 'expense');
    const breakdown = [];
    if (totalHours > 0) {
      breakdown.push({
        label: `Labour (${totalHours}${totalHours === 1 ? ' hr' : ' hrs'} @ £${rate.toFixed(2)}/hr)`,
        amount: parseFloat((totalHours * rate).toFixed(2)),
      });
    }
    expenses.forEach(e => breakdown.push({ label: e.description, amount: e.amount }));
    setInvoicingFromLog(null);
    setLogRate('');
    navigate('/admin/invoices', {
      state: {
        prefill: {
          quoteId: job.id,
          clientEmail: job.email,
          clientName: job.name,
          description: job.services?.join(', ') || 'Horticulture services',
          breakdown,
        },
      },
    });
  }

  async function createInvoice(job) {
    if (!invoiceAmount) return;
    setSaving(true);
    const isRecurring = job.jobType && job.jobType !== 'one-off';
    const ref = await addDoc(collection(db, 'invoices'), {
      quoteId: job.id,
      clientEmail: job.email,
      clientName: job.name,
      clientPhone: job.phone || '',
      address: job.address || '',
      description: invoiceDesc || job.services?.join(', ') || 'Horticulture services',
      amount: parseFloat(invoiceAmount),
      paid: false,
      emailSent: false,
      createdAt: serverTimestamp(),
      reference: `LDH-${job.id.slice(-6).toUpperCase()}`,
      recurring: isRecurring || false,
    });
    await updateDoc(doc(db, 'quotes', job.id), {
      status: isRecurring ? 'active' : 'invoiced',
      invoiceAmount: parseFloat(invoiceAmount),
      invoiceId: ref.id,
      ...(isRecurring ? { lastInvoicedAt: serverTimestamp() } : {}),
    });
    setInvoicing(null);
    setInvoiceAmount('');
    setInvoiceDesc('');
    setSaving(false);
    setSuccess(isRecurring ? `Invoice sent — ${job.name} reset for next cycle` : `Invoice sent to ${job.name}`);
    setTimeout(() => setSuccess(null), 4000);
  }

  async function saveNextDate(jobId) {
    await updateDoc(doc(db, 'quotes', jobId), { nextDate: nextDateDraft || null });
    setSchedulingJob(null);
  }

  async function saveNotes(jobId) {
    await updateDoc(doc(db, 'quotes', jobId), { notes: noteDraft });
    setNoteEditJob(null);
  }

  const filteredJobs = searchQuery.trim()
    ? jobs.filter(j => {
        const q = searchQuery.toLowerCase();
        return j.name?.toLowerCase().includes(q) ||
               j.address?.toLowerCase().includes(q) ||
               j.email?.toLowerCase().includes(q) ||
               j.services?.some(s => s.toLowerCase().includes(q));
      })
    : jobs;

  const activeJobs = filteredJobs.filter(j => j.status === 'active');
  const completeJobs = filteredJobs.filter(j => j.status === 'complete');
  const invoicedJobs = filteredJobs.filter(j => j.status === 'invoiced');
  const paidJobs = filteredJobs.filter(j => j.status === 'paid');
  const upcomingJobs = [...jobs].filter(j => j.nextDate).sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate));

  const jobCardProps = {
    expandedJob, setExpandedJob,
    editingType, typeDraft, setEditingType, setTypeDraft, setJobType,
    clearingLogJob, setClearingLogJob, clearWorkLog,
    loggingEntry, setLoggingEntry, entryDraft, setEntryDraft, openLogForm, saveLogEntry, removeLogEntry,
    invoicingFromLog, setInvoicingFromLog, logRate, setLogRate, createInvoiceFromLog,
    schedulingJob, setSchedulingJob, nextDateDraft, setNextDateDraft, saveNextDate,
    invoicing, setInvoicing, invoiceAmount, setInvoiceAmount, invoiceDesc, setInvoiceDesc, saving, createInvoice,
    noteEditJob, setNoteEditJob, noteDraft, setNoteDraft, saveNotes,
  };

  return (
    <>
      <div className="app-header">
        <div><h1>Garden & Groundworks</h1><div className="subtitle">Active jobs</div></div>
      </div>
      <div className="page-wrap" style={{ paddingTop: 24 }}>
        {!addingClient ? (
          <button className="btn-primary" style={{ marginBottom: 16 }} onClick={() => { setAddingClient(true); setAddingForExistingClient(false); setClientForm({ name: '', email: '', phone: '', address: '', description: '' }); }}>
            + Add client & job
          </button>
        ) : (
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
              {addingForExistingClient ? `Add job for ${clientForm.name}` : 'New client & job'}
            </h3>
            {addingClientError && <div className="error-msg">{addingClientError}</div>}
            <form onSubmit={addClientJob}>
              <div className="form-group">
                <label>Client name *</label>
                <input value={clientForm.name} onChange={e => setClientForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" disabled={addingForExistingClient} />
              </div>
              <div className="form-group">
                <label>Client email *</label>
                <input type="email" value={clientForm.email} onChange={e => setClientForm(f => ({ ...f, email: e.target.value }))} placeholder="client@email.com" disabled={addingForExistingClient} />
              </div>
              <div className="form-group">
                <label>Client phone</label>
                <input value={clientForm.phone} onChange={e => setClientForm(f => ({ ...f, phone: e.target.value }))} placeholder="07…" />
              </div>
              <div className="form-group">
                <label>Client address</label>
                <input value={clientForm.address} onChange={e => setClientForm(f => ({ ...f, address: e.target.value }))} placeholder="Site / billing address" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Job description *</label>
                <input value={clientForm.description} onChange={e => setClientForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Hedge cutting and lawn maintenance" />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button type="submit" className="btn-primary" disabled={addingClientSaving} style={{ flex: 2 }}>
                  {addingClientSaving ? 'Saving...' : 'Add job'}
                </button>
                <button type="button" className="btn-secondary" onClick={() => { setAddingClient(false); setAddingForExistingClient(false); setClientForm({ name: '', email: '', phone: '', address: '', description: '' }); }} style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {success && (
          <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 10,
            padding: '12px 16px', marginBottom: 16, color: '#155724', fontWeight: 600 }}>
            ✅ {success}
          </div>
        )}

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '1rem' }}>🔍</span>
          <input
            type="search"
            placeholder="Search by name, address or service…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ paddingLeft: 38, width: '100%' }}
          />
        </div>

        {/* Upcoming scheduled visits */}
        {upcomingJobs.length > 0 && (
          <>
            <h2 className="section-title" style={{ color: '#1565C0', marginBottom: 8 }}>📅 Upcoming schedule</h2>
            {upcomingJobs.map(j => (
              <div key={j.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '9px 12px', background: '#E3F2FD', borderRadius: 8, marginBottom: 6,
                border: '1px solid #90CAF9' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{j.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#1565C0' }}>
                    {j.services?.join(', ') || 'Horticulture'} · {JOB_TYPE_LABELS[j.jobType] || j.jobType}
                  </div>
                </div>
                <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1565C0' }}>
                  {new Date(j.nextDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                </div>
              </div>
            ))}
            <hr className="divider" />
          </>
        )}

        {activeJobs.length > 0 && (
          <>
            <h2 className="section-title" style={{ color: 'var(--green-mid)' }}>🔧 In progress ({activeJobs.length})</h2>
            {activeJobs.map(j => <JobCard key={j.id} job={j} {...jobCardProps} />)}
          </>
        )}

        {completeJobs.length > 0 && (
          <>
            <h2 className="section-title" style={{ color: 'var(--gold)', marginTop: 16 }}>✓ Complete — awaiting invoice ({completeJobs.length})</h2>
            {completeJobs.map(j => <JobCard key={j.id} job={j} {...jobCardProps} />)}
          </>
        )}

        {invoicedJobs.length > 0 && (
          <>
            <h2 className="section-title" style={{ marginTop: 16 }}>📄 Invoiced ({invoicedJobs.length})</h2>
            {invoicedJobs.map(j => <JobCard key={j.id} job={j} {...jobCardProps} />)}
          </>
        )}

        {paidJobs.length > 0 && (
          <>
            <h2 className="section-title" style={{ marginTop: 16 }}>💰 Paid ({paidJobs.length})</h2>
            {paidJobs.map(j => <JobCard key={j.id} job={j} {...jobCardProps} />)}
          </>
        )}

        {jobs.length === 0 && (
          <div className="empty-state"><p>No active jobs yet. Jobs appear here once a client accepts a quote.</p></div>
        )}
        {jobs.length > 0 && filteredJobs.length === 0 && (
          <div className="empty-state"><p>No jobs match "{searchQuery}".</p></div>
        )}
      </div>
      <AdminNav active="jobs" />
    </>
  );
}

export function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    const q = query(collection(db, 'messages'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const byClient = {};
      msgs.forEach(m => { if (!byClient[m.clientEmail]) byClient[m.clientEmail] = []; byClient[m.clientEmail].push(m); });
      setThreads(Object.entries(byClient).map(([email, msgs]) => ({ email, latest: msgs[0], count: msgs.length })));
    });
  }, []);

  function openThread(email) {
    setActive(email);
    const q = query(collection(db, 'messages'), where('clientEmail', '==', email), orderBy('createdAt', 'asc'));
    onSnapshot(q, snap => setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }

  async function sendReply(e) {
    e.preventDefault();
    if (!reply.trim() || !active) return;
    await addDoc(collection(db, 'messages'), { clientEmail: active, from: 'admin', text: reply.trim(), createdAt: serverTimestamp() });
    setReply('');
  }

  if (active) return (
    <>
      <div className="app-header">
        <button onClick={() => setActive(null)} style={{ background: 'none', color: 'white', fontSize: '1.2rem', width: 'auto', padding: '0 12px 0 0' }}>←</button>
        <div><h1 style={{ fontSize: '0.95rem' }}>{active}</h1><div className="subtitle">Messages</div></div>
      </div>
      <div className="page-wrap" style={{ paddingTop: 24, paddingBottom: 80 }}>
        {messages.map(m => (
          <div key={m.id} style={{ display: 'flex', justifyContent: m.from === 'admin' ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
            <div style={{ maxWidth: '80%', background: m.from === 'admin' ? 'var(--green-mid)' : 'white', color: m.from === 'admin' ? 'white' : 'var(--text-primary)', borderRadius: m.from === 'admin' ? '16px 16px 4px 16px' : '16px 16px 16px 4px', padding: '10px 14px', boxShadow: 'var(--shadow)', fontSize: '0.95rem' }}>{m.text}</div>
          </div>
        ))}
      </div>
      <form onSubmit={sendReply} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderTop: '1px solid var(--border)', padding: '12px 16px', display: 'flex', gap: 8 }}>
        <input value={reply} onChange={e => setReply(e.target.value)} placeholder="Reply..." style={{ flex: 1 }} />
        <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '12px 20px' }}>Send</button>
      </form>
    </>
  );

  return (
    <>
      <div className="app-header"><div><h1>Garden & Groundworks</h1><div className="subtitle">Messages</div></div></div>
      <div className="page-wrap" style={{ paddingTop: 24 }}>
        {threads.length === 0 ? <div className="empty-state"><p>No messages yet.</p></div> : threads.map(t => (
          <div key={t.email} className="card" style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => openThread(t.email)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontWeight: 600 }}>{t.email}</div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t.count} msg{t.count > 1 ? 's' : ''}</span>
            </div>
            <div style={{ fontSize: '1rem', color: 'var(--text-secondary)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{t.latest?.text}</div>
          </div>
        ))}
      </div>
      <AdminNav active="messages" />
    </>
  );
}

export function AdminClients() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'quotes'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => setQuotes(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  const clients = Object.values(quotes.reduce((acc, q) => {
    if (!acc[q.email]) acc[q.email] = { name: q.name, email: q.email, phone: q.phone, address: q.address, jobs: 0 };
    acc[q.email].jobs++;
    return acc;
  }, {}));

  return (
    <>
      <div className="app-header"><div><h1>Garden & Groundworks</h1><div className="subtitle">Clients ({clients.length})</div></div></div>
      <div className="page-wrap" style={{ paddingTop: 24 }}>
        <button
          className="btn-primary"
          style={{ marginBottom: 16 }}
          onClick={() => navigate('/admin/jobs', { state: { openAddClientJob: true } })}
        >
          + Add client & job
        </button>

        {clients.map(c => (
          <div key={c.email} className="card" style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{c.name}</div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{c.email}</div>
            {c.phone && <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{c.phone}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{c.jobs} job request{c.jobs > 1 ? 's' : ''}</div>
              <button
                onClick={() => navigate('/admin/jobs', {
                  state: {
                    openAddClientJob: true,
                    prefillClient: { name: c.name, email: c.email, phone: c.phone || '', address: c.address || '' },
                  },
                })}
                style={{ background: 'none', border: 'none', color: 'var(--green-dark)', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
              >
                + Add job
              </button>
            </div>
          </div>
        ))}
      </div>
      <AdminNav active="clients" />
    </>
  );
}
